import React, { useEffect, useRef, useCallback } from "react";

/**
 * InfiniteMarquee — a true infinite horizontal marquee.
 *
 * Features:
 *  - Duplicates children internally (renders items TWICE) so the loop is seamless.
 *  - Auto-scrolls right → left at a linear rate (configurable via `duration`).
 *  - Pauses on pointer hover (desktop).
 *  - Users can drag / swipe horizontally on any device (mouse or touch).
 *    Auto-scroll resumes shortly after interaction.
 *  - Uses native `overflow-x: auto` under the hood, so pointer / touch drag is
 *    handled by the browser. Auto-scroll advances `scrollLeft` via rAF.
 *  - When `scrollLeft` crosses the halfway point (the first duplicate), we
 *    subtract the half-width instantly. Because the visible content is
 *    identical at both positions, the jump is invisible.
 *
 * Props:
 *   items           : array of anything renderable via `renderItem`.
 *   renderItem      : (item, index) => ReactNode. Each rendered item must have
 *                     a stable width; wrap in a fixed-padding container.
 *   duration        : seconds per full loop of ONE copy of the list (default 30).
 *   resumeDelay     : ms to wait after user interaction before auto-scroll resumes.
 */
export default function InfiniteMarquee({
  items,
  renderItem,
  duration = 30,
  resumeDelay = 1500,
  className = "",
  itemClassName = "",
}) {
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);
  const stateRef = useRef({
    hovered: false,
    interacting: false,
    resumeTimer: null,
    halfWidth: 0,
    pxPerMs: 0,
  });

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return;
    const half = track.scrollWidth / 2;
    stateRef.current.halfWidth = half;
    stateRef.current.pxPerMs = half / (duration * 1000);
  }, [duration]);

  const tick = useCallback((now) => {
    const scroller = scrollerRef.current;
    const s = stateRef.current;
    if (!scroller) return;

    if (lastTimeRef.current === 0) lastTimeRef.current = now;
    const dt = now - lastTimeRef.current;
    lastTimeRef.current = now;

    const shouldAdvance = !s.hovered && !s.interacting && !document.hidden;
    if (shouldAdvance && s.halfWidth > 0) {
      let next = scroller.scrollLeft + s.pxPerMs * dt;
      if (next >= s.halfWidth) next -= s.halfWidth;
      scroller.scrollLeft = next;
    }

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", measure);

    lastTimeRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);

    const stateSnapshot = stateRef.current;
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      window.removeEventListener("resize", measure);
      if (stateSnapshot.resumeTimer) clearTimeout(stateSnapshot.resumeTimer);
    };
  }, [measure, tick, items]);

  // Keep visual continuity when the user drags past the seam
  const onScroll = () => {
    const scroller = scrollerRef.current;
    const s = stateRef.current;
    if (!scroller || s.halfWidth === 0) return;
    if (scroller.scrollLeft <= 0) {
      scroller.scrollLeft = s.halfWidth + scroller.scrollLeft;
    } else if (scroller.scrollLeft >= s.halfWidth * 2) {
      scroller.scrollLeft = scroller.scrollLeft - s.halfWidth;
    }
  };

  const beginInteract = () => {
    const s = stateRef.current;
    s.interacting = true;
    if (s.resumeTimer) {
      clearTimeout(s.resumeTimer);
      s.resumeTimer = null;
    }
  };
  const endInteract = () => {
    const s = stateRef.current;
    s.resumeTimer = setTimeout(() => {
      s.interacting = false;
      s.resumeTimer = null;
    }, resumeDelay);
  };

  const onMouseEnter = () => { stateRef.current.hovered = true; };
  const onMouseLeave = () => { stateRef.current.hovered = false; };

  const doubled = [...items, ...items];

  return (
    <div
      ref={scrollerRef}
      onScroll={onScroll}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDown={beginInteract}
      onPointerUp={endInteract}
      onPointerCancel={endInteract}
      onTouchStart={beginInteract}
      onTouchEnd={endInteract}
      className={`mt-marquee ${className}`}
      data-testid="brand-marquee"
      aria-label="Supported vehicle brands"
      role="region"
    >
      <div ref={trackRef} className="mt-marquee__track" aria-hidden="false">
        {doubled.map((item, i) => (
          <div key={i} className={`mt-marquee__item ${itemClassName}`} aria-hidden={i >= items.length ? "true" : "false"}>
            {renderItem(item, i % items.length)}
          </div>
        ))}
      </div>
    </div>
  );
}
